import { getCalculatedFieldValue } from 'api';
import { FieldTemplate } from 'api/models';
import { FormikState } from 'formik';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { FormValues } from 'routes/Form/FormLayout';
import { isCalculationsLoading, lastTimeFormWasSaved } from 'state/formState';
import { GeneralIds } from 'types/types';

interface UseCalculatedFieldCalculationsProps {
  setFormikState: (
    stateOrCb:
      | FormikState<FormValues>
      | ((state: FormikState<FormValues>) => FormikState<FormValues>),
  ) => void;
  templates: FieldTemplate[];
}

export default function useCalculatedFieldCalculations({
  templates,
  setFormikState,
}: UseCalculatedFieldCalculationsProps) {
  const lastSaved = useRecoilValue(lastTimeFormWasSaved);
  const setCalculationsLoading = useSetRecoilState(isCalculationsLoading);
  const { visitId, formId } = useParams<GeneralIds>();

  useEffect(() => {
    const loadData = async () => {
      setCalculationsLoading(true);
      const calculatedFields = templates.filter(
        field => field.fieldType === 'calculated',
      );

      const calculatedFieldsAliases = calculatedFields.map(f => f.alias);

      const templateMap = new Map<string, FieldTemplate>(
        calculatedFields.map(template => [template.alias, template]),
      );

      const calculatedFieldsUsedInCalculatedFields = calculatedFields.flatMap(
        f => {
          const regex = /(?<=').*?(?=\|CURRENT)/g;
          const matches = f.fieldAttributes?.code?.match(regex) ?? [];
          const calculatedFieldMatches = matches.filter(alias =>
            calculatedFieldsAliases.includes(alias),
          );

          return calculatedFieldMatches;
        },
      );

      calculatedFieldsAliases.forEach(alias => {
        if (calculatedFieldsUsedInCalculatedFields.includes(alias)) {
          return;
        }
        calculatedFieldsUsedInCalculatedFields.push(alias);
      });

      const errors: any = {};
      const values: any = {};

      for await (const alias of calculatedFieldsUsedInCalculatedFields) {
        const fieldTemplateId = templateMap.get(alias)?.id ?? '';
        const { value, errorMessage } = await getCalculatedFieldValue({
          formId: formId ?? '',
          visitId: visitId ?? '',
          fieldTemplateId,
        });

        if (errorMessage) {
          errors[fieldTemplateId] = errorMessage;
        } else {
          values[fieldTemplateId] = value;
        }
      }

      setFormikState((state: any) => ({
        ...state,
        values: { ...state.values, ...values },
        errors: { ...state.errors, ...errors },
      }));
      setCalculationsLoading(false);
    };

    setCalculationsLoading(false);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSaved]);
}
